import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

// ── Static parts products (not in Printify) ───────────────────────────────────
const staticProducts: Record<string, any> = {
  'custom-exhaust': {
    id: 'custom-exhaust', category: 'Parts',
    name: 'Custom Exhaust Quote', price: 0,
    shortDesc: 'Stainless, aluminum, or titanium. Built to your spec.',
    colors: ['Stainless', 'Aluminum', 'Titanium'], sizes: ['Custom'],
    image: '/images/OG W Skull.png', images: [],
  },
  'intercooler-piping': {
    id: 'intercooler-piping', category: 'Parts',
    name: 'Intercooler Piping Kit', price: 0,
    shortDesc: 'Custom bent and welded to your setup.',
    colors: ['Aluminum', 'Stainless'], sizes: ['Custom'],
    image: '/images/OG W Skull.png', images: [],
  },
};

// ── Map raw Printify product to display shape ─────────────────────────────────
function mapPrintify(p: any) {
  const colorOpt = (p.options ?? []).find((o: any) => /color/i.test(o.name));
  const sizeOpt  = (p.options ?? []).find((o: any) => /size/i.test(o.name));

  const colors = colorOpt?.values?.map((v: any) => v.title) ?? ['Standard'];
  const sizes  = sizeOpt?.values?.map((v: any)  => v.title) ?? ['One Size'];

  const enabled  = (p.variants ?? []).filter((v: any) => v.is_enabled);
  const minPrice = enabled.length > 0
    ? Math.min(...enabled.map((v: any) => v.price)) / 100
    : 0;

  const defaultImg = (p.images ?? []).find((i: any) => i.is_default)?.src
    ?? p.images?.[0]?.src
    ?? '/images/OG-Tech Logo.png';

  return {
    id:        p.id,
    category:  'Merch',
    name:      p.title,
    shortDesc: (p.description ?? '').replace(/<[^>]+>/g, '').slice(0, 200),
    price:     minPrice,
    colors,
    sizes,
    image:     defaultImg,
    images:    (p.images ?? []).map((i: any) => i.src),
  };
}

export default function Product() {
  const { id }     = useParams<{ id: string }>();
  const navigate   = useNavigate();
  const { add }    = useCart();

  const [product, setProduct]         = useState<any>(null);
  const [loading, setLoading]         = useState(true);
  const [selectedColor, setColor]     = useState('');
  const [selectedSize, setSize]       = useState('');
  const [qty, setQty]                 = useState(1);
  const [added, setAdded]             = useState(false);
  const [activeImage, setActiveImage] = useState('');

  useEffect(() => {
    if (!id) return;

    // Static product
    if (staticProducts[id]) {
      const p = staticProducts[id];
      setProduct(p);
      setColor(p.colors[0]);
      setSize(p.sizes[0]);
      setActiveImage(p.image);
      setLoading(false);
      return;
    }

    // Printify product
    fetch(`/.netlify/functions/product?id=${id}`)
      .then(r => r.json())
      .then((raw: any) => {
        const p = mapPrintify(raw);
        setProduct(p);
        setColor(p.colors[0]);
        setSize(p.sizes[0]);
        setActiveImage(p.image);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div style={{ paddingTop: 120, textAlign: 'center', fontFamily: 'DM Mono', fontSize: 13, color: 'var(--muted)', letterSpacing: 2 }}>
        LOADING...
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ paddingTop: 120, textAlign: 'center' }}>
        <div style={{ fontFamily: 'Bomber Dreams', fontSize: 48 }}>Product not found</div>
      </div>
    );
  }

  const isPOR = product.price === 0;

  const handleAdd = () => {
    add({
      id:      product.id,
      name:    product.name,
      variant: `${selectedColor} / ${selectedSize}`,
      price:   product.price,
      qty,
      image:   product.image,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div style={{ paddingTop: 64 }}>
      <section style={{ padding: '60px 32px 80px' }}>
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'start',
        }}
          className="product-grid"
        >
          {/* Images */}
          <div>
            <div style={{
              background: 'var(--card)', border: '1px solid var(--border)',
              borderRadius: 12, aspectRatio: '1',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', marginBottom: 12,
            }}>
              <img src={activeImage} alt={product.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>

            {product.images.length > 1 && (
              <div style={{ display: 'flex', gap: 8 }}>
                {product.images.map((img: string, i: number) => (
                  <button key={i} onClick={() => setActiveImage(img)} style={{
                    flex: 1, aspectRatio: '1', padding: 4,
                    border: `1px solid ${activeImage === img ? 'var(--teal)' : 'var(--border)'}`,
                    borderRadius: 8, background: 'var(--card)', cursor: 'pointer',
                    overflow: 'hidden', transition: 'border-color 0.15s',
                  }}>
                    <img src={img} alt={`View ${i + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div style={{
              fontFamily: 'DM Mono', fontSize: 12, letterSpacing: 3,
              color: 'var(--teal)', textTransform: 'uppercase', marginBottom: 10,
            }}>
              {product.category}
            </div>
            <h1 style={{
              fontFamily: 'DM Sans', fontSize: 'clamp(28px, 3.5vw, 42px)',
              fontWeight: 700, letterSpacing: 0.3, marginBottom: 16,
            }}>
              {product.name}
            </h1>
            <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, marginBottom: 28 }}>
              {product.shortDesc}
            </p>

            <div style={{
              fontFamily: 'DM Sans', fontSize: 36, fontWeight: 900,
              color: isPOR ? 'var(--pink)' : 'var(--teal)',
              marginBottom: 28,
            }}>
              {isPOR ? 'Price on Request' : `$${product.price.toFixed(2)}`}
            </div>

            {/* Color */}
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontFamily: 'DM Mono', fontSize: 11, letterSpacing: 2,
                color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 10,
              }}>
                {product.category === 'Parts' ? 'Material' : 'Color'} — <span style={{ color: 'var(--text)' }}>{selectedColor}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {product.colors.map((c: string) => (
                  <button key={c} onClick={() => setColor(c)} style={{
                    fontFamily: 'DM Mono', fontSize: 11, letterSpacing: 1,
                    padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${selectedColor === c ? 'var(--teal)' : 'var(--border)'}`,
                    background: selectedColor === c ? 'var(--teal-dim)' : 'transparent',
                    color: selectedColor === c ? 'var(--teal)' : 'var(--muted)',
                    transition: 'all 0.15s',
                  }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div style={{ marginBottom: 28 }}>
              <div style={{
                fontFamily: 'DM Mono', fontSize: 11, letterSpacing: 2,
                color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 10,
              }}>
                {product.category === 'Parts' ? 'Spec' : 'Size'} — <span style={{ color: 'var(--text)' }}>{selectedSize}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {product.sizes.map((s: string) => (
                  <button key={s} onClick={() => setSize(s)} style={{
                    fontFamily: 'DM Mono', fontSize: 11, letterSpacing: 1,
                    padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${selectedSize === s ? 'var(--teal)' : 'var(--border)'}`,
                    background: selectedSize === s ? 'var(--teal-dim)' : 'transparent',
                    color: selectedSize === s ? 'var(--teal)' : 'var(--muted)',
                    transition: 'all 0.15s',
                  }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Qty + Add */}
            {!isPOR && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
                }}>
                  <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{
                    width: 40, height: 44, background: 'transparent',
                    border: 'none', color: 'var(--muted)', fontSize: 18, cursor: 'pointer',
                  }}>−</button>
                  <span style={{
                    width: 40, textAlign: 'center',
                    fontFamily: 'DM Mono', fontSize: 14, color: 'var(--text)',
                  }}>{qty}</span>
                  <button onClick={() => setQty(q => q + 1)} style={{
                    width: 40, height: 44, background: 'transparent',
                    border: 'none', color: 'var(--muted)', fontSize: 18, cursor: 'pointer',
                  }}>+</button>
                </div>

                <button onClick={handleAdd} style={{
                  flex: 1, fontFamily: 'DM Mono', fontSize: 13, letterSpacing: 2,
                  textTransform: 'uppercase', padding: '12px 24px', borderRadius: 8,
                  background: added ? 'var(--green)' : 'var(--teal)',
                  color: '#000', border: 'none', cursor: 'pointer',
                  transition: 'background 0.2s',
                }}>
                  {added ? 'Added ✓' : 'Add to Cart'}
                </button>
              </div>
            )}

            {isPOR && (
              <button onClick={() => navigate('/contact')} style={{
                width: '100%', fontFamily: 'DM Mono', fontSize: 13, letterSpacing: 2,
                textTransform: 'uppercase', padding: '14px 24px', borderRadius: 8,
                background: 'var(--pink)', color: '#000', border: 'none',
                cursor: 'pointer', marginBottom: 16,
              }}>
                Request a Quote →
              </button>
            )}

            <div style={{
              fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 1,
              color: 'var(--muted)', textAlign: 'center', marginTop: 8,
            }}>
              Built in San Antonio, TX · OG-TECH LLC
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 768px) { .product-grid { grid-template-columns: 1fr !important; gap: 32px !important; } }
        `}</style>
      </section>
    </div>
  );
}
